// 안드로이드 네이티브 앱(WebView) 브리지 유틸.
//
// 앱의 WebView 안에서 열리면 window.MdlApp 객체가 존재하고,
// 브라우저(PC·아이폰)에는 없다. 구버전 앱에는 일부 메서드가 없을 수 있다.
// 그래서 «존재 여부 확인 + try/catch» 로 감싸고, 오류가 나도 «앱 아님» 상태로 동작한다.
//
// MdlApp 메서드는 전부 동기 호출이다. await 하지 않는다.

interface MdlAppBridge {
  stepSupported?: () => boolean;
  stepPermission?: () => boolean;
  stepRequestPermission?: () => void;
  stepToday?: () => number;
  stepSyncNow?: () => void;
  setMemberToken?: (t: string) => void;
  clearMemberToken?: () => void;
  openBatterySettings?: () => void;
}

interface BridgeWindow {
  MdlApp?: MdlAppBridge;
}

export interface StepCallbackWindow {
  onMdlStepSynced?: (steps?: number) => void;
  onMdlStepPermission?: (granted?: boolean) => void;
}

function bridge(): MdlAppBridge | null {
  try {
    const w = window as unknown as BridgeWindow;
    return w.MdlApp ?? null;
  } catch {
    return null;
  }
}

// 앱(WebView) 안에서 열렸는가
export const isApp = (): boolean => bridge() !== null;

// 앱에 만보계 브리지가 들어 있는가 (구버전 앱 판별)
// 구버전 앱은 stepSupported 메서드 자체가 없으므로 «센서 없음»과 구분된다.
export const stepBridgeReady = (): boolean => {
  try {
    return typeof bridge()?.stepSupported === "function";
  } catch {
    return false;
  }
};

// 이 기기에 걸음 센서가 있는가
export const stepSupported = (): boolean => {
  const b = bridge();
  try {
    return b?.stepSupported?.() === true;
  } catch {
    return false;
  }
};

// 걸음 권한이 있는가
export const stepPermission = (): boolean => {
  const b = bridge();
  try {
    return b?.stepPermission?.() === true;
  } catch {
    return false;
  }
};

// 권한 요청 — 결과는 window.onMdlStepPermission 콜백으로 온다
export const stepRequestPermission = (): void => {
  try {
    bridge()?.stepRequestPermission?.();
  } catch {
    /* 무시 */
  }
};

// 지금 이 순간의 오늘 걸음 수 (센서 직접 읽기, 서버 왕복 없음)
export const stepToday = (): number => {
  try {
    const b = bridge();
    if (!b) return 0;
    return Number(b.stepToday?.()) || 0;
  } catch {
    return 0;
  }
};

// 서버로 즉시 업로드 요청 — 결과는 window.onMdlStepSynced 콜백으로 온다
export const stepSyncNow = (): void => {
  try {
    bridge()?.stepSyncNow?.();
  } catch {
    /* 무시 */
  }
};

// 로그인 직후 호출 — 앱이 회원 토큰을 보관해야 백그라운드 업로드가 된다
export const setMemberToken = (t: string): void => {
  try {
    bridge()?.setMemberToken?.(t);
  } catch {
    /* 무시 */
  }
};

// 로그아웃 시 호출 — 앱이 보관한 회원 토큰을 비운다
export const clearMemberToken = (): void => {
  try {
    bridge()?.clearMemberToken?.();
  } catch {
    /* 무시 */
  }
};

// 배터리 최적화 설정 화면 열기
export const openBatterySettings = (): void => {
  try {
    bridge()?.openBatterySettings?.();
  } catch {
    /* 무시 */
  }
};